import React, { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { supabase, consumePasswordRecoveryFlag } from './lib/supabase'
import Login from './pages/Login'
import Calendar from './pages/Calendar'
import Properties from './pages/Properties'
import Crew from './pages/Crew'
import PrintView from './pages/PrintView'
import AllJobsPrintView from './pages/AllJobsPrintView'
import Activity from './pages/Activity'
import PropertyHistory from './pages/PropertyHistory'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import ResetPassword from './pages/ResetPassword'
import Navbar from './components/Navbar'

function RequireAuth({ children, session }) {
  const location = useLocation()
  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  return children
}

// Routes the user to /reset-password whenever Supabase fires the
// PASSWORD_RECOVERY event, regardless of where the email link dropped them.
// Also consumes a module-level flag in case the event fired before mount.
function RecoveryRouter() {
  const navigate = useNavigate()
  const location = useLocation()
  useEffect(() => {
    if (consumePasswordRecoveryFlag() && location.pathname !== '/reset-password') {
      navigate('/reset-password', { replace: true })
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' && window.location.pathname !== '/reset-password') {
        navigate('/reset-password', { replace: true })
      }
    })
    return () => subscription.unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return null
}

function AppLayout({ session }) {
  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
      <Navbar session={session} />
      <div className="flex-1 overflow-hidden">
        <Routes>
          <Route path="/" element={<RequireAuth session={session}><Calendar /></RequireAuth>} />
          <Route path="/properties" element={<RequireAuth session={session}><Properties /></RequireAuth>} />
          <Route path="/crew" element={<RequireAuth session={session}><Crew /></RequireAuth>} />
          <Route path="/activity" element={<RequireAuth session={session}><Activity /></RequireAuth>} />
          <Route path="/property-history" element={<RequireAuth session={session}><PropertyHistory /></RequireAuth>} />
        </Routes>
      </div>
    </div>
  )
}

export default function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <svg className="animate-spin w-8 h-8 text-brand-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    )
  }

  return (
    <BrowserRouter>
      <RecoveryRouter />
      <Routes>
        <Route path="/login" element={session ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/print/:date/:crewId" element={<RequireAuth session={session}><PrintView /></RequireAuth>} />
        <Route path="/print-all/:date" element={<RequireAuth session={session}><AllJobsPrintView /></RequireAuth>} />
        <Route path="/*" element={<AppLayout session={session} />} />
      </Routes>
    </BrowserRouter>
  )
}
